import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, getMaintenanceStatus } from '../services/api';

import {
	ContentWrapper,
	Container,
	LeftContainer,
	RightContainer,
	Title,
	Subtitle,
	Form,
	InputGroup,
	CheckboxGroup,
	CheckboxLabel,
	InputLabel,
	Star,
	ErrorPopup,
	CloseButton,
	ResponsiveKoalaFix,
	EyeIcon,
} from '../style-comps/LoginPage.styles';

import Input from '../components/Input';
import Button from '../components/Button';
import Checkbox from '../components/Checkbox';
import SupportContactModal from '../components/SupportContactModal';

// ── Assets ────────────────────────────────────────────────────────────────
// 左側背景照片 — 換成新 UI 的床鋪照片；如果檔案路徑不同請自行調整
import LoginBgImg from '../Assets/LoginImg.png';
// KozyGuru logo (新 UI 裡放在卡片頂部)
import LogoImg from '../Assets/LogoImg01.png';

const formatPasswordDate = (isoString) => {
	if (!isoString) return '';
	let dateString = isoString;
	if (isoString.includes('T') && !isoString.includes('Z') && !isoString.includes('+')) {
		dateString = isoString + 'Z';
	}
	const date = new Date(dateString);
	const formatter = new Intl.DateTimeFormat('en-AU', {
		timeZone: 'Australia/Sydney',
		year: 'numeric', month: '2-digit', day: '2-digit',
		hour: '2-digit', minute: '2-digit', hour12: false,
	});
	const parts = formatter.formatToParts(date);
	const v = (t) => parts.find(p => p.type === t)?.value;
	return `${v('year')}-${v('month')}-${v('day')} ${v('hour')}:${v('minute')} (Sydney Time)`;
};

const LoginPage = ({ onLogin }) => {
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [rememberMe, setRememberMe] = useState(false);
	const navigate = useNavigate();
	const passwordRef = useRef(null);
	const [showError, setShowError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [failCount, setFailCount] = useState(0);
	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [showSupportModal, setShowSupportModal] = useState(false);
	const [appVersion, setAppVersion] = useState('');

	// ── Remember Me ──────────────────────────────────────────────────────
	useEffect(() => {
		const saved = localStorage.getItem('rememberMe');
		const savedUsername = localStorage.getItem('savedUsername');

		if (saved === 'true' && savedUsername) {
			setUsername(savedUsername);
			setRememberMe(true);
			passwordRef.current?.focus();
		}
	}, []);

	// ── App Version ──────────────────────────────────────────────────────
	useEffect(() => {
		const fetchVersion = async () => {
			try {
				const data = await getMaintenanceStatus();
				if (data?.AppVersion) setAppVersion(data.AppVersion);
			} catch (err) {
				console.error('版本取得失敗:', err);
			}
		};
		fetchVersion();
	}, []);

	// ── Login handler (completely unchanged) ─────────────────────────────
	const handleLogin = async (e) => {
		e.preventDefault();

		try {
			const data = await login(username, password);

			if (data?.Token) {
				console.log('✅ Login successful');

				if (rememberMe) {
					localStorage.setItem('rememberMe', 'true');
					localStorage.setItem('savedUsername', username);
				} else {
					localStorage.removeItem('rememberMe');
					localStorage.removeItem('savedUsername');
				}

				const requireChange = data.RequirePasswordChange === true;
				localStorage.setItem('requirePasswordChange', requireChange ? 'true' : 'false');

				// 清掉上一個用戶的快取
				const allKeys = Object.keys(localStorage);
				allKeys.forEach(key => {
					if (
						key.startsWith("dashboard_reports_") ||
						key.startsWith("cachedUserListings") ||
						key.startsWith("cachedListingInfoList") ||
						key.startsWith("cachedCalendarData") ||
						key.startsWith("cachedReservationMap") ||
						key.startsWith("calendarCacheTime") ||
						key.startsWith("reportViewed-") ||
						key.startsWith("userData")
					) {
						localStorage.removeItem(key);
					}
				});

				onLogin?.();
				if (requireChange) {
					navigate('/dashboard/setting');
				} else {
					navigate('/dashboard');
				}
			} else {
				console.error('❌ Login failed: Invalid response format');
				setErrorMessage('Login failed. Please try again.');
				setShowError(true);
			}
		} catch (error) {
			console.group('🔴 Login Error');
			console.error('Error:', error);
			console.error('Response:', error.response);
			console.error('Request:', error.request);
			console.error('Config:', error.config);
			console.groupEnd();

			let errorMsg = 'An unexpected error occurred';

			if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
				errorMsg = 'Unable to connect to server. Please check your internet connection.';
			} else if (!error.response) {
				errorMsg = 'Server is not responding. Please try again later.';
			} else {
				const { data, status } = error.response;

				switch (status) {
					case 401:
						const savedPwDate = localStorage.getItem("passwordUpdatedAt");
						let msg = data?.message || 'Invalid email or password';
						if (savedPwDate) {
							const formatted = formatPasswordDate(savedPwDate);
							msg += `\nYour password was last updated on ${formatted}.`;
						}
						errorMsg = msg;
						break;
					case 403:
						errorMsg = data?.message || 'Access denied. Your account may not be authorized.';
						break;
					case 500:
						errorMsg = 'Server error. Please try again later.';
						break;
					default:
						errorMsg =
							typeof data === 'string'
								? data
								: data?.message || data?.error || `Error: ${status}`;
				}
			}

			setErrorMessage(errorMsg);
			const newFailCount = failCount + 1;
			setFailCount(newFailCount);
			if (newFailCount >= 2) {
				setShowForgotPassword(true);
			}
			setShowError(true);
		}
	};

	const handleForgotPassword = () => {
		setShowSupportModal(true);
	};

	// ── Render ────────────────────────────────────────────────────────────
	return (
		<>
			<ResponsiveKoalaFix />

			<Container>
				{/* ── Left: photo panel ── */}
				<LeftContainer bg={LoginBgImg}>
					<div style={{
						position: 'absolute',
						bottom: '40px',
						left: '40px',
						right: '40px',
						zIndex: 1,
					}}>
						<h2 style={{
							color: '#ffffff',
							fontSize: '28px',
							fontWeight: '800',
							lineHeight: '1.3',
							margin: '0 0 8px',
							fontFamily: 'Roboto, sans-serif',
							textShadow: '0 2px 8px rgba(0,0,0,0.3)',
						}}>
							Manage every stay,<br />the kozy way.
						</h2>
						<p style={{
							color: 'rgba(255,255,255,0.85)',
							fontSize: '13px',
							margin: 0,
							fontFamily: 'Roboto, sans-serif',
							textShadow: '0 1px 4px rgba(0,0,0,0.3)',
						}}>
							Reservations · Listings · Revenue — all in one portal
						</p>
					</div>
				</LeftContainer>

				{/* ── Right: white panel + card ── */}
				<RightContainer>
					<ContentWrapper>
						{/* Logo */}
						<img
							src={LogoImg}
							alt="KozyGuru"
							style={{ width: '160px', height: 'auto', marginBottom: '4px' }}
						/>

						{/* Heading */}
						<Title>Welcome Back</Title>
						<Subtitle>Please log in to continue</Subtitle>

						{/* Form */}
						<Form onSubmit={handleLogin}>
							{/* Email */}
							<InputGroup>
								<InputLabel>Email<Star>*</Star></InputLabel>
								<Input
									type="email"
									value={username}
									onChange={(e) => setUsername(e.target.value)}
									placeholder="Email"
									autoCapitalize="off"
								/>
							</InputGroup>

							{/* Password */}
							<InputGroup>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0px' }}>
									<InputLabel style={{ position: 'static', background: 'transparent', padding: 0, marginBottom: '0' }}>
										Password<Star>*</Star>
									</InputLabel>
									{showForgotPassword && (
										<span
											onClick={handleForgotPassword}
											style={{
												color: '#61cbc9',
												fontSize: '13px',
												cursor: 'pointer',
												textDecoration: 'none',
												fontWeight: '500',
												fontFamily: 'Roboto, sans-serif',
											}}
										>
											Forgot password?
										</span>
									)}
								</div>
								<div style={{ position: 'relative', width: '100%' }}>
									<Input
										ref={passwordRef}
										type={showPassword ? 'text' : 'password'}
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										placeholder="Password"
										style={{ paddingRight: '44px' }}
									/>
									<EyeIcon
										src={
											showPassword
												? '/Assets/Eyes-open.png'
												: '/Assets/Eyes-close.png'
										}
										alt="Toggle visibility"
										onClick={() => setShowPassword(!showPassword)}
									/>
								</div>
							</InputGroup>

							{/* Remember me */}
							<CheckboxGroup style={{ marginBottom: '16px' }}>
								<Checkbox
									isChecked={rememberMe}
									onChange={(e) => setRememberMe(e.target.checked)}
								/>
								<CheckboxLabel>Remember me</CheckboxLabel>
							</CheckboxGroup>

							{/* Login button — full width */}
							<Button
								label="Login"
								style={{
									backgroundColor: '#61cbc9',
									color: '#ffffff',
									width: '100%',
									padding: '14px',
									fontSize: '16px',
									fontWeight: '600',
									border: 'none',
									borderRadius: '8px',
									cursor: 'pointer',
								}}
							/>
						</Form>

						{/* Version */}
						<p style={{
							marginTop: '24px',
							fontSize: '12px',
							color: '#bbb',
							fontFamily: 'Roboto, sans-serif',
							textAlign: 'center',
						}}>
							© {new Date().getFullYear()} KozyGuru. Admin Portal{appVersion ? ` v${appVersion}` : ''}
						</p>
					</ContentWrapper>
				</RightContainer>
			</Container>

			{/* Error popup */}
			{showError && (
				<ErrorPopup show={showError}>
					<div style={{ whiteSpace: 'pre-line' }}>{errorMessage}</div>
					<CloseButton onClick={() => setShowError(false)}>×</CloseButton>
				</ErrorPopup>
			)}

			{/* Forgot password / contact support */}
			<SupportContactModal
				isOpen={showSupportModal}
				onClose={() => setShowSupportModal(false)}
				accountEmail={username}
			/>
		</>
	);
};

export default LoginPage;